import "./TestFiltersComponent.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import CommonService from "../../../shared/services/common.service";
import {Field, FieldProps, Form, Formik, FormikProps, FormikValues} from "formik";
import FormikAutoCompleteDropdown
    from "../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FormikDatePickerComponent from "../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import formValidationRules from "../../../shared/services/validation.service";
import * as Yup from "yup";
import commonService from "../../../shared/services/common.service";

interface TestFiltersComponentProps {
    filtersData: any;
    onFiltersApply: (filterData: any) => void;
    clearAllFilters: () => void;
}

const validationSchema = Yup.object().shape({
    startDate: Yup.mixed().nullable().when("endDate", {
        is: (endDate: Date) => {
            return Boolean(endDate)
        },
        then: Yup.mixed().nullable().required("Start date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    }),
    endDate: Yup.mixed().nullable().when("startDate", {
        is: (startDate: Date) => {
            return Boolean(startDate)
        },
        then: Yup.mixed().nullable().required("End date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    })
}, [['startDate', 'endDate']]);

const TestFiltersComponent = (props: TestFiltersComponentProps) => {

    const {filtersData, onFiltersApply, clearAllFilters} = props;
    const [dataFiltersInitialState, setDataFiltersInitialState] = useState(filtersData);
    const {
        userList,
        facilityStatusList,
        // sampleTypesList, equipmentTypeList,
        sampleTypeWithCollectionDevices,
    } = useSelector((state: IRootReducerState) => state.staticData);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const onFiltersApplyHandler = useCallback((values: any) => {
        const payload = {...values};
        payload.sampleTypes = payload?.sampleTypeObjs?.map((item: any) => item?.sampleType);
        payload.sampleCollectionDevices = payload?.sampleCollectionDeviceObjs?.map((item: any) => item?.code);
        payload.statuses = payload?.statusesObjs?.map((item: any) => item?.code);
        payload.createdByIds = payload.createdByObjs.map((item: any) => item.id);
        payload.startDate = payload.startDate ? CommonService.convertDateFormat(values['startDate'], 'YYYY-MM-DD') : "";
        payload.endDate = payload.endDate ? CommonService.convertDateFormat(values['endDate'], 'YYYY-MM-DD') : "";
        onFiltersApply(payload);
    }, [onFiltersApply]);

    useEffect(() => {
        setDataFiltersInitialState(filtersData);
    }, [filtersData]);

    return (
        <div className={'test-panel-filters-component screen t-form-screen'}>
            <>
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Filters"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef.current && formikRef.current.resetForm();
                             clearAllFilters();
                         }}
                    >
                        Clear all
                    </div>
                </div>
                <div className="t-form t-form-drawer">
                    <Formik
                        initialValues={dataFiltersInitialState}
                        enableReinitialize={true}
                        onSubmit={onFiltersApplyHandler}
                        innerRef={formikRef}
                        validationSchema={validationSchema}
                    >
                        {({isSubmitting, values, isValid, setFieldValue, validateForm, errors, dirty}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [values, errors, validateForm]);

                            return (
                                <Form noValidate={true} className={"patient-filters-holder"}>
                                    <div className="t-form-controls-wrapper">
                                        <Field name={'sampleTypeObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Sample Type'}
                                                        multiple={true}
                                                        data={sampleTypeWithCollectionDevices}
                                                        valueKey={"id"}
                                                        placeholder={'Select Sample Type'}
                                                        formikField={field}
                                                        getOptionLabel={(option) => (commonService.capitalizeFirstLetter(option.sampleType)) || ''}
                                                        onUpdate={(value: any) => {
                                                            setFieldValue('sampleCollectionDeviceObjs', []);
                                                        }
                                                        }
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'sampleCollectionDeviceObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Sample Collection Device'}
                                                        multiple={true}
                                                        data={commonService.extractCollectionDevicesFromSampleType(values?.sampleTypeObjs?.map((sample: any) => sample.sampleType), sampleTypeWithCollectionDevices) || []}
                                                        valueKey={"code"}
                                                        placeholder={'Select Sample Collection Device'}
                                                        formikField={field}
                                                        getOptionLabel={(option) => (commonService.capitalizeFirstLetterOfEachWord(option?.title)) || ''}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'createdByObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Added by'}
                                                        multiple={true}
                                                        data={userList}
                                                        valueKey={"id"}
                                                        placeholder={'Select Added by'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option?.firstName + ' ' + option?.lastName) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'statusesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Test Status'}
                                                        multiple={true}
                                                        data={facilityStatusList}
                                                        valueKey={"code"}
                                                        placeholder={'Select Test Status'}
                                                        formikField={field}
                                                        getOptionLabel={(option) => (option?.title)}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'startDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'Start Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'endDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'End Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="drawer-actions form-action-section">
                                        <ButtonComponent variant={"contained"}
                                                         fullWidth={true}
                                                         type={"submit"}
                                                         disabled={!isValid || !dirty}
                                        >
                                            Apply
                                        </ButtonComponent>
                                    </div>
                                </Form>
                            )
                        }
                        }
                    </Formik>
                </div>
            </>
        </div>
    );

};

export default TestFiltersComponent;
